import { useEffect, useRef } from "react";
import { ExternalLink } from "lucide-react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Shared loader (same one the AddressAutocomplete uses, deduped via module-level promise)
let _mapsPromise = null;
function loadMaps() {
  if (window.google?.maps) return Promise.resolve(window.google);
  if (_mapsPromise) return _mapsPromise;
  _mapsPromise = new Promise((resolve, reject) => {
    if (!KEY) return reject(new Error("REACT_APP_GOOGLE_MAPS_API_KEY missing"));
    const cb = "__fieldlinkGmapsReady";
    if (window[cb]) {
      // Already pending from elsewhere — poll
      const poll = setInterval(() => {
        if (window.google?.maps) { clearInterval(poll); resolve(window.google); }
      }, 80);
      return;
    }
    if (document.querySelector("script[data-fieldlink-gmaps]")) {
      const poll = setInterval(() => {
        if (window.google?.maps) { clearInterval(poll); resolve(window.google); }
      }, 80);
      return;
    }
    window[cb] = () => { resolve(window.google); delete window[cb]; };
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&libraries=places&callback=${cb}&loading=async`;
    s.async = true;
    s.defer = true;
    s.dataset.fieldlinkGmaps = "1";
    s.onerror = () => reject(new Error("Google Maps script failed"));
    document.head.appendChild(s);
  });
  return _mapsPromise;
}

/**
 * MapPreview — small interactive Google Map showing a single pin.
 * Uses Maps JavaScript API (already loaded by AddressAutocomplete) — no extra API to enable.
 */
export function StaticMapPreview({ lat, lng, address, height = 180, zoom = 16 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (lat == null || lng == null || !ref.current) return;
    let cancelled = false;
    loadMaps().then((g) => {
      if (cancelled || !ref.current) return;
      const pos = { lat, lng };
      if (!mapRef.current) {
        mapRef.current = new g.maps.Map(ref.current, {
          center: pos,
          zoom,
          disableDefaultUI: true,
          zoomControl: true,
          clickableIcons: false,
          gestureHandling: "cooperative",
          styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }],
        });
      } else {
        mapRef.current.setCenter(pos);
      }
      if (markerRef.current) markerRef.current.setMap(null);
      markerRef.current = new g.maps.Marker({
        position: pos,
        map: mapRef.current,
        icon: {
          path: g.maps.SymbolPath.CIRCLE,
          scale: 9,
          fillColor: "#FF5F15",
          fillOpacity: 1,
          strokeColor: "#FFFFFF",
          strokeWeight: 3,
        },
      });
    }).catch(() => {/* silent */});
    return () => { cancelled = true; };
  }, [lat, lng, zoom]);

  if (lat == null || lng == null) return null;

  return (
    <div className="mt-3 border border-zinc-200 relative overflow-hidden bg-zinc-100" data-testid="map-preview">
      <div ref={ref} style={{ height, width: "100%" }} />
      <a
        href={directionsLink({ lat, lng, address })}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 bg-[#09090B] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 hover:bg-[#FF5F15] transition-colors shadow-md"
        data-testid="map-directions-link"
      >
        <ExternalLink className="w-3 h-3" /> Directions
      </a>
    </div>
  );
}

/** Build a Google Maps "Get Directions" URL — works on web, iOS, Android. */
export function directionsLink({ lat, lng, address }) {
  if (lat != null && lng != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(lat + "," + lng)}`;
  }
  if (address) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  }
  return "https://www.google.com/maps";
}

/** Compact "Get Directions" pill used inline on Job cards / detail rows. */
export function GetDirectionsLink({ lat, lng, address, className = "", testid = "get-directions" }) {
  if (!address && (lat == null || lng == null)) return null;
  return (
    <a
      href={directionsLink({ lat, lng, address })}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#FF5F15] hover:text-[#E65100] transition-colors ${className}`}
      data-testid={testid}
    >
      <ExternalLink className="w-3 h-3" /> Directions
    </a>
  );
}
